import { useState, useEffect, useRef } from "react";
import { sfClient } from "../services/salesforceApi";
import { getEnv } from "../config/getEnv";
import { SOBJECT_PROJECT } from "../config/sobjectNames";

const describeCache = {};
const describePromises = {};

export async function fetchDescribe(sObjectName = SOBJECT_PROJECT) {
  if (describeCache[sObjectName]) return describeCache[sObjectName];
  if (describePromises[sObjectName]) return describePromises[sObjectName];

  const baseUrl = getEnv("REACT_APP_SF_BASE_URL") || "";
  const apiVersion = getEnv("REACT_APP_SF_API_VERSION") || "";
  const url = `${baseUrl}/services/data/${apiVersion}/sobjects/${sObjectName}/describe`;

  describePromises[sObjectName] = sfClient
    .get(url)
    .then((res) => {
      describeCache[sObjectName] = res.data;
      delete describePromises[sObjectName];
      return describeCache[sObjectName];
    })
    .catch((err) => {
      delete describePromises[sObjectName];
      throw err;
    });

  return describePromises[sObjectName];
}

function extractPicklistOptions(describeData, fieldName) {
  const fields = describeData?.fields;
  if (!Array.isArray(fields)) return [];

  const field = fields.find((f) => f.name === fieldName);
  if (!field || !Array.isArray(field.picklistValues)) return [];

  const values = field.picklistValues
    .filter((plv) => plv.active)
    .map((plv) => ({ label: plv.label, value: plv.value }));

  const index = values.findIndex((v) => v.value === "Other");
  if (index !== -1) {
    values.push(values.splice(index, 1)[0]);
  }

  return values;
}

/**
 * Returns active picklist values for a given Salesforce field label.
 * The describe call is made once per SObject and cached at module level.
 *
 * @param {string|null} fieldLabel
 * @param {string} [sObjectName] — defaults to Project__c
 * @returns {{ options: Array<{label: string, value: string}>, loading: boolean, error: Error|null }}
 */
export function usePicklist(fieldLabel, sObjectName = SOBJECT_PROJECT) {
  const [data, setData] = useState();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(!!fieldLabel);
  const [error, setError] = useState(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (cancelledRef.current) return;
    setOptions(extractPicklistOptions(data, fieldLabel));
  }, [fieldLabel]);
  useEffect(() => {
    setLoading(true);
    setError(null);

    fetchDescribe(sObjectName)
      .then((data) => {
        setData(data);
      })
      .catch((err) => {
        if (cancelledRef.current) return;
        setError(err);
        setLoading(false);
      });
    return () => {
      cancelledRef.current = true;
    };
  }, [sObjectName]);

  return { options, loading, error };
}

/**
 * Fetches the describe response once per SObject, then builds a map of
 * { [fieldLabel]: string[] } for every label in the provided array.
 *
 * @param {string[]} fieldLabels
 * @param {string} [sObjectName] — defaults to Project__c
 * @returns {{ picklistMap: Record<string, Array<{label: string, value: string}>>, loading: boolean, error: Error|null }}
 */
export function usePicklistMap(fieldLabels, sObjectName = SOBJECT_PROJECT) {
  const [picklistMap, setPicklistMap] = useState({});
  const [loading, setLoading] = useState(
    Array.isArray(fieldLabels) && fieldLabels.length > 0,
  );
  const [error, setError] = useState(null);
  const cancelledRef = useRef(false);

  const labelsKey = Array.isArray(fieldLabels)
    ? fieldLabels.slice().sort().join("|")
    : "";

  useEffect(() => {
    if (!labelsKey) {
      setPicklistMap({});
      setLoading(false);
      return;
    }

    cancelledRef.current = false;
    setLoading(true);
    setError(null);

    const labels = labelsKey.split("|");

    fetchDescribe(sObjectName)
      .then((data) => {
        if (cancelledRef.current) return;
        const map = {};
        labels.forEach((label) => {
          map[label] = extractPicklistOptions(data, label);
        });
        setPicklistMap(map);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelledRef.current) return;
        setError(err);
        setLoading(false);
      });

    return () => {
      cancelledRef.current = true;
    };
  }, [labelsKey, sObjectName]);

  return { picklistMap, loading, error };
}

/**
 * Recursively walks field configs and injects `options` from the picklistMap
 * for any field that has a `picklistLabel` property.
 */
export function resolvePicklistFields(fields, picklistMap) {
  if (!Array.isArray(fields)) return fields;
  return fields.map((f) => {
    if (f.picklistLabel && picklistMap[f.picklistLabel]?.length > 0) {
      return { ...f, options: picklistMap[f.picklistLabel] };
    }
    if (f.fields) {
      return { ...f, fields: resolvePicklistFields(f.fields, picklistMap) };
    }
    return f;
  });
}

export default usePicklist;
